import { z } from "zod";

import {
  LlmProviderValues,
  LlmRouteClassValues,
  LlmUsageEntityTypeValues,
  LlmUsageEventStatusValues,
  LlmUsageFailureReasonValues,
  LlmUsageSubsystemValues,
  LlmUsageWorkloadValues,
} from "../types/enums.js";

const NonEmptyStringSchema = z.string().min(1);
const IsoTimestampSchema = z.string().datetime({ offset: true });
const NonNegativeIntegerSchema = z.number().int().nonnegative();

export const LlmUsageEventDebugArtifactReferenceSchema = z
  .object({
    storageBucket: NonEmptyStringSchema,
    storageKey: NonEmptyStringSchema,
    contentType: NonEmptyStringSchema,
    sizeBytes: NonNegativeIntegerSchema.optional(),
  })
  .strict();

export const LlmUsageEventDebugArtifactSchema = z
  .object({
    usageEventId: NonEmptyStringSchema,
    provider: z.enum(LlmProviderValues),
    modelId: NonEmptyStringSchema,
    subsystem: z.enum(LlmUsageSubsystemValues),
    workload: z.enum(LlmUsageWorkloadValues),
    entityType: z.enum(LlmUsageEntityTypeValues),
    entityId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema.optional(),
    routeClass: z.enum(LlmRouteClassValues),
    eventStatus: z.enum(LlmUsageEventStatusValues),
    failureReason: z.enum(LlmUsageFailureReasonValues).optional(),
    failureDetail: z.string().optional(),
    promptVersion: NonEmptyStringSchema.optional(),
    schemaVersion: NonEmptyStringSchema.optional(),
    modelRegistryVersion: NonEmptyStringSchema.optional(),
    routingPolicyVersion: NonEmptyStringSchema.optional(),
    prompt: z.string(),
    structuredOutputText: z.string().optional(),
    parsedOutput: z.record(z.unknown()).optional(),
    providerResponse: z.record(z.unknown()).optional(),
    createdAt: IsoTimestampSchema,
  })
  .strict();

export const LlmUsageEventSchema = z
  .object({
    usageEventId: NonEmptyStringSchema,
    provider: z.enum(LlmProviderValues),
    modelId: NonEmptyStringSchema,
    subsystem: z.enum(LlmUsageSubsystemValues),
    workload: z.enum(LlmUsageWorkloadValues),
    entityType: z.enum(LlmUsageEntityTypeValues),
    entityId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema.optional(),
    routeClass: z.enum(LlmRouteClassValues),
    eventStatus: z.enum(LlmUsageEventStatusValues),
    failureReason: z.enum(LlmUsageFailureReasonValues).optional(),
    failureDetail: z.string().optional(),
    modelRegistryVersion: NonEmptyStringSchema.optional(),
    routingPolicyVersion: NonEmptyStringSchema.optional(),
    promptVersion: NonEmptyStringSchema.optional(),
    schemaVersion: NonEmptyStringSchema.optional(),
    inputTokenCount: NonNegativeIntegerSchema.optional(),
    cachedInputTokenCount: NonNegativeIntegerSchema.optional(),
    outputTokenCount: NonNegativeIntegerSchema.optional(),
    totalTokenCount: NonNegativeIntegerSchema.optional(),
    estimatedCostMicros: NonNegativeIntegerSchema.optional(),
    oracleId: NonEmptyStringSchema.optional(),
    userId: NonEmptyStringSchema.optional(),
    importId: NonEmptyStringSchema.optional(),
    sourceIntegrationRunId: NonEmptyStringSchema.optional(),
    debugArtifact: LlmUsageEventDebugArtifactReferenceSchema.optional(),
    createdAt: IsoTimestampSchema,
  })
  .strict()
  .superRefine((event, context) => {
    if (event.eventStatus === "FAILED" && !event.failureReason) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["failureReason"],
        message: "failureReason is required when eventStatus is FAILED.",
      });
    }

    if (event.eventStatus === "COMPLETED" && event.failureReason) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["failureReason"],
        message: "failureReason must be omitted when eventStatus is COMPLETED.",
      });
    }

    if (
      event.eventStatus === "COMPLETED" &&
      event.inputTokenCount == null &&
      event.outputTokenCount == null &&
      event.totalTokenCount == null
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inputTokenCount"],
        message:
          "At least one token count is required when eventStatus is COMPLETED.",
      });
    }
  });

export const LlmUsagePricingRowSchema = z
  .object({
    provider: z.enum(LlmProviderValues),
    modelId: NonEmptyStringSchema,
    displayName: NonEmptyStringSchema,
    inputUsdPerMillionTokens: z.number().nonnegative(),
    cachedInputUsdPerMillionTokens: z.number().nonnegative(),
    outputUsdPerMillionTokens: z.number().nonnegative(),
    isFallback: z.boolean(),
  })
  .strict();

export const LlmUsageEventPageSchema = z
  .object({
    items: z.array(LlmUsageEventSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const LlmUsagePricingTableSchema = z
  .object({
    provider: z.enum(LlmProviderValues),
    pricingVersion: NonEmptyStringSchema,
    refreshedAt: IsoTimestampSchema,
    isFallback: z.boolean(),
    warning: z.string().optional(),
    rows: z.array(LlmUsagePricingRowSchema),
  })
  .strict();
